// Builds the org chart of a customer from its stores (the flat
// `GET /businesses/?customer=` list).
//
// The hierarchy lives on Persona: a store's `parent_company_uuid` is the uuid
// of its parent *persona*, which may be the customer's own persona or the
// persona of another store. The store sharing the customer's persona is the
// customer itself acting as a store, so it is merged into the root node
// (`rootStore`) instead of becoming a child.

const COMPANY_TYPE_ORDER = { headquarters: 0, independent: 1, branch: 2 };

const compareStores = (a, b) =>
  (COMPANY_TYPE_ORDER[a.business.company_type] ?? 3) - (COMPANY_TYPE_ORDER[b.business.company_type] ?? 3) ||
  (a.business.display_name ?? '').localeCompare(b.business.display_name ?? '', undefined, { sensitivity: 'base' });

const sortTree = (nodes) => {
  nodes.sort(compareStores);
  nodes.forEach((node) => sortTree(node.children));
  return nodes;
};

/**
 * @returns {{ customer, rootStore: object|null, children: Array<{ business, children }> }}
 */
export function buildOrganizationTree(customer, businesses = [], { includeDeleted = false } = {}) {
  const rootPersona = customer?.persona?.uuid ?? null;
  const visible = businesses.filter((business) => includeDeleted || !business.is_deleted);

  const isRootStore = (business) => Boolean(rootPersona) && business.persona_uuid === rootPersona;
  const rootStore =
    visible.find((business) => isRootStore(business) && !business.is_deleted) ??
    visible.find(isRootStore) ??
    null;

  const nodes = visible
    .filter((business) => business !== rootStore)
    .map((business) => ({ business, children: [] }));

  // First store of each persona is the one its branches hang from.
  const nodeByPersona = new Map();
  nodes.forEach((node) => {
    const persona = node.business.persona_uuid;
    if (persona && persona !== rootPersona && !nodeByPersona.has(persona)) {
      nodeByPersona.set(persona, node);
    }
  });

  // Parent unknown (customer persona, no parent, deleted/out-of-scope parent)
  // or a store of the same persona: no parent, so it hangs from the root.
  const parentOf = new Map();
  nodes.forEach((node) => {
    const parent = nodeByPersona.get(node.business.parent_company_uuid);
    if (parent && parent.business.persona_uuid !== node.business.persona_uuid) {
      parentOf.set(node, parent);
    }
  });

  // Bad data could make a loop (A under B under A), which would hide those
  // stores; a store whose ancestors lead back to itself goes to the root.
  const isInCycle = (node) => {
    const seen = new Set();
    for (let current = parentOf.get(node); current && !seen.has(current); current = parentOf.get(current)) {
      if (current === node) return true;
      seen.add(current);
    }
    return false;
  };

  const children = [];
  nodes.forEach((node) => {
    const parent = parentOf.get(node);
    if (parent && !isInCycle(node)) {
      parent.children.push(node);
    } else {
      children.push(node);
    }
  });

  return { customer, rootStore, children: sortTree(children) };
}
